import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { MaintenanceView } from './MaintenanceView.js';
import { LandingView } from './LandingView.js';
import type { MeUser } from '../../shared/protocol.js';
import { JOIN_ROUTES } from '../../shared/registration.js';

type PublicClosure = { closed: boolean; message: string; endsAt: number | null };

/** While the arena is closed the page is checked this often, so it opens by itself. */
const CLOSURE_POLL_MS = 15_000;

/** `notice`: e.g. a guest's visit has ended. */
export function LoginView(props: { onLogin: (user: MeUser) => void; notice?: string | null }) {
  const [closure, setClosure] = useState<PublicClosure | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  // The server does not answer (e.g. system maintenance): a maintenance page until it is back.
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const load = () =>
      api<{ closure: PublicClosure }>('GET', '/api/closure')
        .then((r) => {
          setOffline(false);
          setClosure(r.closure);
        })
        .catch(() => setOffline(true));
    void load();
    const timer = setInterval(() => void load(), CLOSURE_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  if (offline) return <MaintenanceView title={t.closure.offlineTitle} message={t.closure.offlineMessage} endsAt={null} />;

  // Closed: the maintenance page; the login form stays available (the administrator can
  // still use the arena).
  if (closure?.closed) {
    return (
      <MaintenanceView message={closure.message} endsAt={closure.endsAt}>
        {showLogin ? (
          <LoginForm onLogin={props.onLogin} compact />
        ) : (
          <button class="btn btn-ghost btn-small" onClick={() => setShowLogin(true)}>
            {t.login.title}
          </button>
        )}
      </MaintenanceView>
    );
  }

  // The public front page (user request 2026-09-30); the login form opens in a dialog.
  return <LandingView onLogin={props.onLogin} notice={props.notice} />;
}

/** `dialog`: inside the front page login dialog (a title instead of the big logo). */
export function LoginForm(props: { onLogin: (user: MeUser) => void; compact?: boolean; dialog?: boolean; notice?: string | null }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Registration with an invite code (decision 2026-09-28), unless the administrator has turned it off.
  const [codeRegistration, setCodeRegistration] = useState(false);

  useEffect(() => {
    if (props.compact) return;
    api<{ codeRegistration: boolean }>('GET', '/api/join/status')
      .then((r) => setCodeRegistration(r.codeRegistration))
      .catch(() => {});
  }, []);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ user: MeUser }>('POST', '/api/auth/login', { username, password });
      props.onLogin(r.user);
    } catch (err) {
      setError(t.errors[err instanceof ApiError ? err.code : 'network']);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class={`card login ${props.compact ? 'login-compact' : ''} ${props.dialog ? 'login-dialog' : ''}`} onSubmit={submit}>
      {props.dialog && <h2 id="login-title">{t.login.title}</h2>}
      {!props.compact && !props.dialog && (
        <>
          <h1 class="login-logo">
            <Logo size="large" animated />
          </h1>
          <p class="subtitle">{t.login.subtitle}</p>
          {props.notice && (
            <p class="success center-text" role="status">
              {props.notice}
            </p>
          )}
        </>
      )}
      <label>
        {t.login.username}
        <input
          value={username}
          onInput={(e) => setUsername(e.currentTarget.value)}
          autocomplete="username"
          autocapitalize="none"
          spellcheck={false}
          required
          autofocus
        />
      </label>
      <label>
        {t.login.password}
        <input
          type="password"
          value={password}
          onInput={(e) => setPassword(e.currentTarget.value)}
          autocomplete="current-password"
          required
        />
      </label>
      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}
      <button class="btn btn-primary" type="submit" disabled={busy}>
        {busy ? t.login.submitting : t.login.submit}
      </button>
      {!props.compact && <p class="hint">{t.login.noAccount}</p>}
      {codeRegistration && (
        <a class="btn btn-ghost" href={`#/${JOIN_ROUTES.code}`}>
          {t.login.register} →
        </a>
      )}
      {!props.compact && (
        <a class="btn btn-ghost" href={`#/${JOIN_ROUTES.guest}`}>
          {t.guest.loginLink} →
        </a>
      )}
    </form>
  );
}
